/**
 * OMNI FLOW DIRECTOR — what is executable now, and what has been recorded
 * as changing (`flow-director-1`).
 *
 * The construction convention this breaks is the **priority list**. A
 * dispatch board normally ranks: most critical first, longest float last.
 * This one refuses to. Fronts arrive ordered by instance id — a total order
 * with no interpretive content — and this panel renders that order
 * untouched. There is no score in the payload and none is reconstructed
 * here.
 *
 * It sits below the classification it re-expresses rather than above it,
 * because it is the same answer in the charter's vocabulary plus one lane
 * the source read does not carry: what has actually been RECORDED as
 * moving. Collapsed by default — a reader who wants the six-status view
 * already has it above.
 *
 * Deliberately not rendered here: safe parallel work and resequencing
 * candidates. Both are already on this tab from the source classification,
 * and showing two independently-fetched snapshots of one fact side by side
 * would invite a reader to diff them as though the difference meant
 * something.
 */
import { useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { ContractMismatchCallout } from "./RuntimePrimitives";
import {
  FLOW_CONTRACT,
  presentFlow,
  bandDriftFronts,
} from "./flowPresentation";
import {
  Compass,
  ChevronDown,
  History,
  Users,
  AlertTriangle,
  ListOrdered,
  Lock,
  HelpCircle,
} from "lucide-react";

/**
 * Band tone. `active` and `done` are their own — neither is `good`, because
 * a green badge on running or finished work turns a "what can I start"
 * board into a list of things to start again.
 */
const BAND_TONE = {
  good: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  attention: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  bad: "border-red-500/40 text-red-300 bg-red-500/10",
  unknown: "border-border text-muted-foreground bg-muted/40",
  active: "border-sky-500/40 text-sky-300 bg-sky-500/10",
  done: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  neutral: "border-border text-muted-foreground",
};

const tone = (key) => BAND_TONE[key] || BAND_TONE.unknown;

const Caveat = ({ children, testId }) =>
  children ? (
    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid={testId}>
      {children}
    </p>
  ) : null;

const WorkFront = ({ front }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1" data-testid="flow-front">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <span className="text-xs font-medium min-w-0">
        {front.instanceName || front.instanceId}
      </span>
      <span className="inline-flex flex-wrap items-center gap-1">
        {/* Both names, always together: the pair is what makes them one fact. */}
        <Badge variant="outline" className={`text-[10px] ${tone(front.band.tone)}`}>
          {front.band.label}
        </Badge>
        {front.status ? (
          <Badge
            variant="outline"
            className="text-[10px] border-border text-muted-foreground"
            data-testid="flow-front-status"
          >
            {front.status}
          </Badge>
        ) : null}
        <Badge
          variant="outline"
          className={`text-[10px] ${tone(front.clearance.tone)}`}
          data-testid="flow-clearance"
        >
          {front.clearance.label}
        </Badge>
      </span>
    </div>
    {front.governingConstraint ? (
      <p className="text-[11px] text-muted-foreground">{front.governingConstraint}</p>
    ) : null}
    {front.clearance.applicable === false && front.clearance.basis ? (
      <Caveat testId="clearance-basis">{front.clearance.basis}</Caveat>
    ) : null}
    {front.storedDiffers ? (
      <Caveat testId="stored-differs">
        Stored: {front.storedReadinessState}. {front.storedDiffersNote}
      </Caveat>
    ) : null}
    {front.bandAgrees === false ? (
      <Alert className="border-amber-500/40 bg-amber-500/10 py-2" data-testid="band-drift">
        <AlertTriangle className="h-3.5 w-3.5" />
        <AlertDescription className="text-[10px] leading-relaxed">
          {front.bandDriftNote}
        </AlertDescription>
      </Alert>
    ) : null}
    {front.unlockedByConstraints.length ? (
      <p className="text-[10px] text-muted-foreground" data-testid="front-unlocked-by">
        Released by: {front.unlockedByConstraints.join(" · ")}
      </p>
    ) : null}
  </div>
);

const Transition = ({ entry }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-0.5" data-testid="flow-transition">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-[11px] font-medium">{entry.actionLabel}</span>
      <span className="text-[10px] font-mono text-muted-foreground">
        {entry.occurredAt ? entry.occurredAt.slice(0, 19).replace("T", " ") : "time not recorded"}
      </span>
    </div>
    <p className="text-[10px] text-muted-foreground">
      {entry.instanceId}
      {entry.actorId ? ` · ${entry.actorId}` : ""}
      {entry.trigger ? ` · ${entry.trigger}` : ""}
    </p>
    {entry.oldState || entry.newState ? (
      <p className="text-[11px] font-mono">
        {entry.oldState || "—"} → {entry.newState || "—"}
      </p>
    ) : null}
    {entry.state ? <p className="text-[11px] font-mono">{entry.state}</p> : null}
    {entry.blockingReason ? (
      <p className="text-[10px] text-muted-foreground">{entry.blockingReason}</p>
    ) : null}
    {entry.unmappedFields.length ? (
      <Caveat testId="transition-unmapped">
        The record also held: {entry.unmappedFields.join(", ")} — named rather than dropped, because
        this contract does not carry them.
      </Caveat>
    ) : null}
    {entry.withheldFields ? (
      <Caveat testId="transition-withheld">
        Withheld: {entry.withheldFields.fields.join(", ")} — {entry.withheldFields.reason}
      </Caveat>
    ) : null}
  </div>
);

const FlowDirectorPanel = ({ payload, error }) => {
  const [open, setOpen] = useState(false);

  if (error) {
    return (
      <Card className="border-border" data-testid="flow-director">
        <CardContent className="py-2.5 px-4">
          <p className="text-xs text-muted-foreground">
            {error.kind === "disabled"
              ? "The flow director is not enabled in this environment."
              : error.kind === "not-entitled"
                ? "The flow director is not part of your plan."
                : error.kind === "no-access"
                  ? "Your role does not include the flow director."
                  : "The flow director did not load. The classification above is unaffected."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const flow = presentFlow(payload);
  if (!flow) return null;
  const drift = bandDriftFronts(flow);
  const transitions = flow.transitions;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-border" data-testid="flow-director">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
          >
            <span className="min-w-0">
              <span className="text-sm font-semibold flex items-center gap-1.5">
                <Compass className="w-4 h-4 text-primary" />
                What is executable now
              </span>
              <span className="text-[10px] text-muted-foreground">
                The same classification in the charter’s band names, plus what has been recorded as
                changing
              </span>
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="px-4 pb-4 space-y-3 border-t border-border pt-3">
            <ContractMismatchCallout received={flow.contractVersion} supported={FLOW_CONTRACT} />

            {/* BANDS — counts with both names on each */}
            <section className="space-y-1.5" data-testid="flow-bands">
              <div className="flex flex-wrap gap-1.5">
                {flow.bands.map((band) => (
                  <Badge
                    key={band.key}
                    variant="outline"
                    className={`text-[10px] ${tone(band.tone)}`}
                    data-testid="flow-band"
                  >
                    {band.label}: {band.count}
                    {band.mappedFrom ? (
                      <span className="opacity-70"> · {band.mappedFrom}</span>
                    ) : null}
                  </Badge>
                ))}
              </div>
              <Caveat testId="band-basis">{flow.bandBasis}</Caveat>
              <Caveat>{flow.bandNote}</Caveat>
              {flow.bands.some((band) => band.instanceIdsTruncated) ? (
                <Caveat testId="band-ids-truncated">
                  At least one band’s instance listing is cut short. The counts above are complete;
                  the listings behind them are not.
                </Caveat>
              ) : null}
              {/* The two tokens this vocabulary shares with other contracts
                  in this product. CLEARED is not a requirement status, and
                  NOT_ESTABLISHED here is unscoreable readiness — not the
                  causality posture the provider ledger uses under the same
                  word. Both are loaded elsewhere; the note keeps them from
                  being read across. */}
              <Caveat testId="clearance-note">{flow.clearanceNote}</Caveat>
            </section>

            {/* ORDERING — the promise, stated where it can be checked */}
            <p
              className="text-[10px] text-muted-foreground flex items-start gap-1.5"
              data-testid="flow-ordering"
            >
              <ListOrdered className="w-3 h-3 mt-0.5 shrink-0" />
              <span className="leading-relaxed">
                Ordered by {flow.orderingKey.join(", ")}. {flow.orderingNote}
              </span>
            </p>

            {drift.length ? (
              <Alert className="border-amber-500/40 bg-amber-500/10" data-testid="flow-drift-summary">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-[11px] leading-relaxed">
                  {drift.length} front{drift.length === 1 ? "" : "s"} carry a band that does not
                  match the mapping this response published. Each is marked below and shown as
                  received.
                </AlertDescription>
              </Alert>
            ) : null}

            {/* WORK FRONTS — in the server's order, never re-sorted */}
            {flow.workFrontsWithheld ? (
              <p className="text-[11px] text-muted-foreground" data-testid="fronts-withheld">
                {flow.workFrontCount} work fronts. {flow.workFrontsWithheldNote}
              </p>
            ) : flow.hasFronts ? (
              <div className="space-y-1.5">
                {flow.workFronts.map((front) => (
                  <WorkFront key={front.instanceId} front={front} />
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground" data-testid="fronts-empty">
                No work front is classified on this project.
              </p>
            )}

            {/* RECORDED TRANSITIONS — layered, and never silently empty */}
            <section className="space-y-1.5" data-testid="flow-transitions">
              <p className="text-[11px] font-medium flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" />
                What has been recorded as changing
              </p>
              {transitions.authorized ? (
                <>
                  {transitions.headline ? (
                    <p className="text-[11px] text-muted-foreground" data-testid="transitions-empty">
                      {transitions.headline}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {transitions.entries.map((entry) => (
                        <Transition key={entry.auditId} entry={entry} />
                      ))}
                    </div>
                  )}
                  {transitions.truncated ? (
                    <Caveat testId="transitions-truncated">
                      {transitions.count} recorded events; the listing above is cut short.
                    </Caveat>
                  ) : null}
                </>
              ) : (
                <div className="space-y-1" data-testid="transitions-refused">
                  <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                    <Lock className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{transitions.headline}</span>
                  </p>
                  <Caveat>{transitions.basis}</Caveat>
                </div>
              )}
              <Caveat testId="replayability">{transitions.replayability}</Caveat>
              {transitions.blockedWithoutRecordedHistory ? (
                <div className="space-y-0.5" data-testid="blocked-without-history">
                  <p className="text-[11px]">
                    {transitions.blockedWithoutRecordedHistory.determined
                      ? `${transitions.blockedWithoutRecordedHistory.count} blocked front(s) carry no recorded history.`
                      : "Whether any blocked front has no recorded history could not be determined."}
                  </p>
                  <Caveat>{transitions.blockedWithoutRecordedHistory.note}</Caveat>
                </div>
              ) : null}
            </section>

            {/* RESOURCE COMPATIBILITY — rule-less, not writer-less */}
            {flow.resourceCompatibility ? (
              <section className="space-y-1" data-testid="resource-compatibility">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Crew compatibility: {flow.resourceCompatibility.value}
                </p>
                <Caveat>{flow.resourceCompatibility.basis}</Caveat>
                {flow.resourceCompatibility.writersPresent.map((writer, index) => (
                  <p key={index} className="text-[10px] text-muted-foreground" data-testid="crew-writer">
                    {writer.fact} → <span className="font-mono">{writer.column}</span>
                  </p>
                ))}
              </section>
            ) : null}

            {/* UNKNOWNS */}
            {flow.unknowns.length ? (
              <section className="space-y-1" data-testid="flow-unknowns">
                <p className="text-[11px] font-medium flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5" />
                  Named gaps
                </p>
                {flow.unknowns.map((unknown, index) => (
                  <div key={index} className="space-y-0.5" data-testid="flow-unknown">
                    <p className="text-[11px]">
                      {unknown.display.label}
                      {unknown.constraintFamily ? (
                        <span className="text-muted-foreground"> · {unknown.constraintFamily}</span>
                      ) : null}
                    </p>
                    <Caveat>{unknown.detail || unknown.note}</Caveat>
                  </div>
                ))}
              </section>
            ) : null}

            {flow.conflictCount ? (
              <p className="text-[10px] text-muted-foreground" data-testid="flow-conflicts">
                {flow.conflictCount} constraint conflict{flow.conflictCount === 1 ? "" : "s"} stand
                unresolved on this project.
                {flow.conflictsTruncated ? " The listing is cut short." : ""}
              </p>
            ) : null}

            {flow.missingInformation.length ? (
              <section className="space-y-0.5" data-testid="flow-missing">
                <p className="text-[11px] font-medium">What this read could not establish</p>
                {flow.missingInformation.map((sentence, index) => (
                  <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                    · {sentence}
                  </p>
                ))}
              </section>
            ) : null}

            <p className="text-[10px] text-muted-foreground" data-testid="flow-fingerprint">
              Composed {flow.computedAt ? flow.computedAt.slice(0, 19).replace("T", " ") : "at an unrecorded moment"}
              {flow.sourceFingerprint ? ` · ${flow.sourceFingerprint.slice(0, 12)}` : ""}
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default FlowDirectorPanel;
